import React, { useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Grid,
  Card,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import FormatSizeIcon from "@mui/icons-material/FormatSize";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useLocation, useNavigate } from "react-router-dom";
import { printHtml } from "../utils/htmlToPrint";

const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 16;

export default function EntryCard() {

  const location = useLocation();
  const { students } = location.state || [];
  const { entryList } = location.state || [];
  const { note } = location.state || "";
  const { times } = location.state || {};
  const temID = "entry-card";
  const toCamelCase = (str) => {
    return str
      ?.toLowerCase()
      .replace(/[^a-zA-Z0-9 ]/g, '')
      .split(' ')
      .map((word, index) =>
        index === 0
          ? word.charAt(0).toUpperCase() + word.slice(1)
          : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join(' ');
  };

  const navigate = useNavigate();
  // --- Customization controls ---
  const [isLandscape, setIsLandscape] = useState(false);
  const [borderColor, setBorderColor] = useState("#000000");
  const [fontSize, setFontSize] = useState(10);

  const cardWidth = isLandscape ? 450 : 300;

  const increaseFontSize = () =>
    setFontSize((prev) => Math.min(MAX_FONT_SIZE, prev + 1));
  const decreaseFontSize = () =>
    setFontSize((prev) => Math.max(MIN_FONT_SIZE, prev - 1));

  // const handlePrint = () => {
  //       setTimeout(() => {
  //           window.print();
  //       }, 100);
  //   };

  const cell = { border: `1px double ${borderColor}`, px: 1, py: 0.5, fontSize: `${fontSize}px`, fontWeight: 600 };

  return (
    <Box>
      {/* Print page orientation follows the Landscape/Portrait toggle below */}
      <style>
        {`@media print { @page {fontSize: ${fontSize}px, size: ${isLandscape ? "landscape" : "portrait"}; } }`}
      </style>

      {/* Controls: hidden when printing */}
      <Paper
        elevation={2}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "center",
          maxWidth: "1200px",
          margin: "auto",
          mb: 3,
          p: 2,
          borderRadius: 3,
          "@media print": { display: "none" },
        }}
      >
        <Button
          size="small"
          variant={isLandscape ? "contained" : "outlined"}
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ textTransform: "none" }}
        >
          Back
        </Button>

        <Button
          size="small"
          variant={isLandscape ? "contained" : "outlined"}
          startIcon={<FormatSizeIcon />}
          onClick={() => setIsLandscape((prev) => !prev)}
          sx={{ textTransform: "none" }}
        >
          {isLandscape ? "Landscape" : "Portrait"}
        </Button>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            Border Color:
          </Typography>
          <input
            type="color"
            value={borderColor}
            onChange={(e) => setBorderColor(e.target.value)}
            style={{
              width: 32,
              height: 32,
              padding: 0,
              border: "1px solid #ccc",
              borderRadius: 4,
              cursor: "pointer",
            }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mr: 0.5 }}>
            Font Size:
          </Typography>
          <Tooltip title="Decrease font size">
            <span>
              <IconButton
                size="small"
                onClick={decreaseFontSize}
                disabled={fontSize <= MIN_FONT_SIZE}
                sx={{ border: "1px solid", borderColor: "divider" }}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="body2" sx={{ width: 32, textAlign: "center" }}>
            {fontSize}px
          </Typography>
          <Tooltip title="Increase font size">
            <span>
              <IconButton
                size="small"
                onClick={increaseFontSize}
                disabled={fontSize >= MAX_FONT_SIZE}
                sx={{ border: "1px solid", borderColor: "divider" }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip>
            <IconButton
              size="small"
              variant="contained"
              color="primary"
              onClick={() => { printHtml(temID); window.location.reload() }}
            >
              <PrintIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      <Box id={temID}
        sx={{
          px: 3,
          "& .MuiTypography-root": {
            color: '#000',
            fontSize: `${fontSize}px`,
          },
        }}
      >
        <Grid container spacing={2} justifyContent={'space-around'}>
          {(students || []).map((student, i) => (
            <Grid item key={student.id}>
              <Card
                elevation={3}
                sx={{
                  width: cardWidth,
                  boxShadow: "0 0px 0px rgba(0,0,0,0.2)",
                  px: 2
                }}>

                {/* Header */}
                <Box textAlign="center" mb={2}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: 'nowrap', flexDirection: "row", alignItems: "center" }}>
                    <img
                      src={student?.logo}
                      alt="School Logo"
                      style={{ width: 80, height: 70, marginTop: 10, borderRadius: 50 }}
                    />
                    <Box>
                      <Typography variant="subtitle1" align="center" fontWeight="bold">
                        {student?.school_name}
                      </Typography>
                      <Typography variant="caption" align="center">
                        {toCamelCase(student?.school_address)} | Phone: {student?.school_phone || "-"}</Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 0.6 }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="secondary"
                    textTransform="uppercase"
                    marginBottom={-1}
                  >
                    Entry Card
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {/* Student Info */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Box>
                    <Typography variant="body2">
                      <strong>Name:</strong> {student?.name || "..........."}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Father's Name:</strong> {student?.father || ".........."}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Mother's Name:</strong> {student?.mother || "........"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2">
                      <strong>Session:</strong> {student?.academicYear || "........."}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Class:</strong> {student?.standard || "......."} | {student?.section}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Roll No.:</strong> {student?.rollNumber || student?.id}
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 1 }} />
                {/* Schedule Table */}
                <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Examination Schedule
                  </Typography>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    {[times?.startTime, times?.endTime].filter(Boolean).join(" - ")}
                  </Typography>
                </Box>
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
                    <TableRow>
                      <TableCell sx={cell}>Date</TableCell>
                      <TableCell sx={cell}>Day</TableCell>
                      <TableCell sx={cell}>Subject</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(entryList || []).map((row, i) => (
                      <TableRow key={i}>
                        <TableCell sx={cell}>
                          {row?.examDate
                            ? new Date(row.examDate).toLocaleDateString("en-GB")
                            : "-"}
                        </TableCell>
                        <TableCell sx={cell}>{row?.days || "-"}</TableCell>
                        <TableCell sx={cell}>{toCamelCase(row?.subject) || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Typography variant="caption" sx={{ whiteSpace: "pre-wrap" }}>
                  <strong>Note:</strong> {toCamelCase(note) || ""}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
        {/* Print Button */}
        {/* <Button
          variant="contained"
          color="secondary"
          startIcon={<PrintIcon />}
          sx={{
            position: "absolute",
            bottom: 20,
            right: 20,
            "@media print": { display: "none" },
          }}
          onClick={() => { printHtml(temID); window.location.reload() }}
        >
          Print Entry Card
        </Button> */}
      </Box>
    </Box>
  );
}